import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import Modal from '@/shared/components/Modal';
import { isYoutubeUrl } from '@/shared/utils/youtube';

import { YOUTUBE_INVALID_URL_ERROR } from './constants';

type YoutubeEmbedDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (url: string) => void;
};

const YoutubeEmbedDialogComponent: React.FC<YoutubeEmbedDialogProps> = ({
	open,
	onOpenChange,
	onSubmit,
}) => {
	const [url, setUrl] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);

	const isValid = useMemo(() => isYoutubeUrl(url), [url]);
	const showError = url.trim().length > 0 && !isValid;

	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (!nextOpen) setUrl('');
			onOpenChange(nextOpen);
		},
		[onOpenChange]
	);

	const handleSubmit = useCallback(
		(event: React.FormEvent) => {
			event.preventDefault();
			if (!isValid) return;

			onSubmit(url);
			handleOpenChange(false);
		},
		[isValid, onSubmit, url, handleOpenChange]
	);

	const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setUrl(event.target.value);
	}, []);

	/** Radix would focus the header close button first; the URL field is what the user wants. */
	const handleOpenAutoFocus = useCallback((event: Event) => {
		event.preventDefault();
		inputRef.current?.focus();
	}, []);

	return (
		<Modal
			open={open}
			onOpenChange={handleOpenChange}
			titleContent="Embed a YouTube video"
			contentClassName="max-w-lg"
			// The Modal's scroll viewport is `pt-0` and clips overflow, which cuts the input's focus ring.
			viewportClassName="pt-2"
			contentProps={{ onOpenAutoFocus: handleOpenAutoFocus }}
		>
			<form
				onSubmit={handleSubmit}
				className="flex flex-col gap-3"
			>
				<Input
					ref={inputRef}
					value={url}
					onChange={handleChange}
					placeholder="Paste a YouTube link"
				/>
				{showError && <span className="text-sm text-destructive">{YOUTUBE_INVALID_URL_ERROR}</span>}
				<div className="flex justify-end gap-2">
					<Button
						type="button"
						variant="outline"
						onClick={() => handleOpenChange(false)}
					>
						Cancel
					</Button>
					<Button
						type="submit"
						disabled={!isValid}
					>
						Embed
					</Button>
				</div>
			</form>
		</Modal>
	);
};

export const YoutubeEmbedDialog = React.memo(YoutubeEmbedDialogComponent);

YoutubeEmbedDialog.displayName = 'YoutubeEmbedDialog';
